@if (loading) {
  <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  <div class="loading-message">Loading cross-reference graph...</div>
}

@if (!loading && !graphExists) {
  <mat-card class="empty-state">
    <mat-card-content>
      <mat-icon class="empty-icon">hub</mat-icon>
      <h3>No Cross-Reference Graph</h3>
      <p>Build the cross-reference graph from Project Settings to see page relationships.</p>
    </mat-card-content>
  </mat-card>

  @if (registryExists) {
    <div class="elements-standalone">
      <div class="elements-standalone-header">
        <span class="elements-title">
          <mat-icon>category</mat-icon>
          Shared Elements
          @if (sharedElements.length > 0) {
            <span class="elements-count">({{ sharedElements.length }})</span>
          }
        </span>
        @if (registryBuiltAt) {
          <span class="registry-built-at">Built {{ registryBuiltAt | date: 'short' }}</span>
        }
        <button
          mat-icon-button
          matTooltip="Rebuild element registry"
          aria-label="Rebuild element registry"
          (click)="buildRegistry()"
          [disabled]="rebuildingRegistry"
          class="rebuild-button"
        >
          <mat-icon>{{ rebuildingRegistry ? 'hourglass_empty' : 'refresh' }}</mat-icon>
        </button>
      </div>

      @if (elementsOnCurrentPage.length > 0) {
        <div class="current-page-elements">
          <span class="section-label">On this page:</span>
          @for (el of elementsOnCurrentPage; track el.elementId) {
            <mat-chip-set>
              <mat-chip class="page-element-chip" [highlighted]="false">
                <mat-icon matChipAvatar>{{ getElementIcon(el.elementType) }}</mat-icon>
                <span class="chip-label">{{ el.canonicalName }}</span>
                @if (el.identityClass === 'generic') {
                  <span class="identity-badge generic">generic</span>
                }
                <span class="appearance-count">{{ el.appearances.length }} pages</span>
              </mat-chip>
            </mat-chip-set>
          }
        </div>
      }

      @if (sharedElements.length > 0) {
        <mat-accordion>
          @for (el of sharedElements; track el.elementId) {
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-icon class="element-type-icon">{{ getElementIcon(el.elementType) }}</mat-icon>
                  {{ el.canonicalName }}
                  @if (el.identityClass === 'generic') {
                    <span class="identity-badge generic">generic</span>
                  }
                </mat-panel-title>
                <mat-panel-description>
                  {{ el.elementType }} · {{ el.appearances.length }} pages{{
                    el.confidence ? ' · ' + el.confidence : ''
                  }}
                </mat-panel-description>
              </mat-expansion-panel-header>
              <div class="appearance-list">
                @for (app of el.appearances; track app.fileId + '-' + app.page) {
                  <div
                    class="appearance-item"
                    role="button"
                    tabindex="0"
                    (click)="navigateToPage(app.fileId, app.page)"
                    (keydown.enter)="navigateToPage(app.fileId, app.page)"
                  >
                    <span class="page-ref">Page {{ app.page }}</span>
                    <span class="projection-badge">{{ app.projectionType }}</span>
                    @if (app.description) {
                      <span class="appearance-desc">{{ app.description }}</span>
                    }
                  </div>
                }
              </div>
            </mat-expansion-panel>
          }
        </mat-accordion>
      }
    </div>
  }
}

@if (!loading && graphExists) {
  <div class="graph-container">
    <div class="graph-toolbar">
      <div class="graph-stats">
        <span class="stat">
          <mat-icon>circle</mat-icon>
          {{ nodeCount }} pages
        </span>
        <span class="stat">
          <mat-icon>arrow_right_alt</mat-icon>
          {{ edgeCount }} references
        </span>
        @if (builtAt) {
          <span class="stat built-at"> Built {{ builtAt | date: 'short' }} </span>
        }
        <button
          mat-icon-button
          matTooltip="Rebuild cross-reference graph"
          aria-label="Rebuild cross-reference graph"
          (click)="rebuildGraph()"
          [disabled]="rebuilding"
          class="rebuild-button"
        >
          <mat-icon>{{ rebuilding ? 'hourglass_empty' : 'refresh' }}</mat-icon>
        </button>
        <button
          mat-icon-button
          matTooltip="Run cross-examination"
          aria-label="Run cross-examination"
          (click)="runCrossExamination()"
          [disabled]="runningExam"
          class="rebuild-button"
        >
          <mat-icon>{{ runningExam ? 'hourglass_empty' : 'fact_check' }}</mat-icon>
        </button>
        @if (examExists) {
          <button
            mat-icon-button
            matTooltip="Toggle findings panel"
            aria-label="Toggle findings panel"
            (click)="findingsDrawerOpen = !findingsDrawerOpen"
            class="toolbar-icon-button"
          >
            <mat-icon
              [style.color]="examContradictions > 0 ? '#c62828' : examDiscrepancies > 0 ? '#f57c00' : '#388e3c'"
            >
              {{ examContradictions > 0 ? 'error' : examDiscrepancies > 0 ? 'warning' : 'check_circle' }}
            </mat-icon>
          </button>
        }
      </div>
      <div class="graph-actions">
        @if (viewMode === 'graph') {
          <button mat-icon-button matTooltip="Fit to view" (click)="fitGraph()" class="toolbar-icon-button">
            <mat-icon>fit_screen</mat-icon>
          </button>
          <button
            mat-icon-button
            matTooltip="Center on current page"
            (click)="centerOnCurrent()"
            class="toolbar-icon-button"
          >
            <mat-icon>my_location</mat-icon>
          </button>
        }
        <div class="toolbar-mode-controls">
          <mat-button-toggle-group
            [value]="viewMode"
            (change)="onViewModeChange($event.value)"
            class="toolbar-toggle-group"
            hideSingleSelectionIndicator
          >
            <mat-button-toggle value="graph" matTooltip="Interactive graph" aria-label="Graph view">
              <mat-icon>hub</mat-icon>
            </mat-button-toggle>
            <mat-button-toggle value="data" matTooltip="Raw JSON data" aria-label="JSON view">
              <mat-icon>data_object</mat-icon>
            </mat-button-toggle>
          </mat-button-toggle-group>
          <mat-button-toggle-group
            [value]="graphPerspective"
            (change)="onPerspectiveChange($event.value)"
            class="toolbar-toggle-group"
            hideSingleSelectionIndicator
          >
            <mat-button-toggle
              value="cross-refs"
              matTooltip="Cross-references"
              aria-label="Cross-references perspective"
            >
              <mat-icon>arrow_right_alt</mat-icon>
            </mat-button-toggle>
            <mat-button-toggle value="elements" matTooltip="Shared elements" aria-label="Shared elements perspective">
              <mat-icon>category</mat-icon>
            </mat-button-toggle>
            <mat-button-toggle value="combined" matTooltip="Combined" aria-label="Combined perspective">
              <mat-icon>layers</mat-icon>
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>
      </div>
    </div>

    @if (viewMode === 'graph') {
      <div class="graph-canvas-area">
        <div class="cytoscape-wrapper">
          <div #cyContainer class="cytoscape-container"></div>
        </div>

        @if (elementsDrawerOpen && graphPerspective !== 'cross-refs') {
          <div class="elements-drawer" [style.width.px]="drawerWidth">
            <div class="drawer-resize-handle" (mousedown)="onDrawerResizeStart($event)"></div>
            <div class="elements-drawer-header">
              <span class="elements-title">
                <mat-icon>category</mat-icon>
                Shared Elements
                @if (sharedElements.length > 0) {
                  <span class="elements-count">({{ sharedElements.length }})</span>
                }
              </span>
              <div class="elements-drawer-actions">
                <button
                  mat-icon-button
                  matTooltip="Build element registry"
                  aria-label="Build element registry"
                  (click)="buildRegistry()"
                  [disabled]="rebuildingRegistry"
                  class="rebuild-button"
                >
                  <mat-icon>{{ rebuildingRegistry ? 'hourglass_empty' : 'refresh' }}</mat-icon>
                </button>
                <button
                  mat-icon-button
                  matTooltip="Close panel"
                  aria-label="Close elements panel"
                  (click)="elementsDrawerOpen = false"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>

            @if (registryBuiltAt) {
              <span class="registry-built-at">Built {{ registryBuiltAt | date: 'short' }}</span>
            }

            <div class="elements-drawer-body">
              @if (!registryExists) {
                <div class="elements-empty">No element registry found. Click the refresh button to build one.</div>
              }

              @if (elementsOnCurrentPage.length > 0) {
                <div class="current-page-elements">
                  <span class="section-label">On this page:</span>
                  @for (el of elementsOnCurrentPage; track el.elementId) {
                    <mat-chip-set>
                      <mat-chip
                        class="page-element-chip"
                        (click)="highlightElement(selectedElement?.elementId === el.elementId ? null : el)"
                        [highlighted]="selectedElement?.elementId === el.elementId"
                      >
                        <mat-icon matChipAvatar>{{ getElementIcon(el.elementType) }}</mat-icon>
                        <span class="chip-label">{{ el.canonicalName }}</span>
                        @if (el.identityClass === 'generic') {
                          <span class="identity-badge generic">generic</span>
                        }
                        <span class="appearance-count">{{ el.appearances.length }} pages</span>
                      </mat-chip>
                    </mat-chip-set>
                  }
                </div>
              }

              @if (sharedElements.length > 0) {
                <mat-accordion>
                  @for (el of sharedElements; track el.elementId) {
                    <mat-expansion-panel>
                      <mat-expansion-panel-header>
                        <mat-panel-title>
                          <mat-icon class="element-type-icon">{{ getElementIcon(el.elementType) }}</mat-icon>
                          {{ el.canonicalName }}
                          @if (el.identityClass === 'generic') {
                            <span class="identity-badge generic">generic</span>
                          }
                        </mat-panel-title>
                        <mat-panel-description>
                          {{ el.elementType }} · {{ el.appearances.length }} pages{{
                            el.confidence ? ' · ' + el.confidence : ''
                          }}
                        </mat-panel-description>
                      </mat-expansion-panel-header>
                      <div class="appearance-list">
                        @for (app of el.appearances; track app.fileId + '-' + app.page) {
                          <div
                            class="appearance-item"
                            role="button"
                            tabindex="0"
                            (click)="navigateToPage(app.fileId, app.page)"
                            (keydown.enter)="navigateToPage(app.fileId, app.page)"
                          >
                            <span class="page-ref">Page {{ app.page }}</span>
                            <span class="projection-badge">{{ app.projectionType }}</span>
                            @if (app.description) {
                              <span class="appearance-desc">{{ app.description }}</span>
                            }
                          </div>
                        }
                      </div>
                      <div class="element-actions">
                        <button
                          mat-stroked-button
                          (click)="highlightElement(selectedElement?.elementId === el.elementId ? null : el)"
                        >
                          <mat-icon>{{
                            selectedElement?.elementId === el.elementId ? 'visibility_off' : 'visibility'
                          }}</mat-icon>
                          {{ selectedElement?.elementId === el.elementId ? 'Clear highlight' : 'Highlight on graph' }}
                        </button>
                      </div>
                    </mat-expansion-panel>
                  }
                </mat-accordion>
              }
            </div>
          </div>
        }

        @if (findingsDrawerOpen && examExists) {
          <div class="findings-drawer" [style.width.px]="drawerWidth">
            <div class="drawer-resize-handle" (mousedown)="onDrawerResizeStart($event)"></div>
            <div class="findings-drawer-header">
              <span class="findings-title">
                <mat-icon>fact_check</mat-icon>
                Cross-Examination
                @if (examFindings.length > 0) {
                  <span class="findings-count">({{ examFindings.length }})</span>
                }
              </span>
              <div class="findings-drawer-actions">
                <button
                  mat-icon-button
                  matTooltip="Copy all findings as JSON"
                  aria-label="Copy all findings as JSON"
                  (click)="copyAllFindingsJson()"
                  [disabled]="examFindings.length === 0"
                >
                  <mat-icon>content_copy</mat-icon>
                </button>
                <button
                  mat-icon-button
                  matTooltip="Re-run cross-examination"
                  aria-label="Re-run cross-examination"
                  (click)="runCrossExamination()"
                  [disabled]="runningExam"
                  class="rebuild-button"
                >
                  <mat-icon>{{ runningExam ? 'hourglass_empty' : 'refresh' }}</mat-icon>
                </button>
                <button
                  mat-icon-button
                  matTooltip="Close panel"
                  aria-label="Close findings panel"
                  (click)="findingsDrawerOpen = false"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>

            <div class="diagnostic-banner">
              <div class="diagnostic-meta-row">
                <span class="diagnostic-label">{{ getEnvironmentLabel() }}</span>
                <span class="diagnostic-separator">·</span>
                <span>{{ loadedProjectId }}</span>
                @if (examBuiltAt) {
                  <span class="diagnostic-separator">·</span>
                  <span>{{ examBuiltAt | date: 'short' }}</span>
                }
                <span class="diagnostic-separator">·</span>
                <span>{{ examEdgesExamined }} edges</span>
              </div>
              <button
                mat-icon-button
                class="diagnostic-copy-btn"
                matTooltip="Copy diagnostic metadata"
                aria-label="Copy diagnostic metadata"
                (click)="copyDiagnosticMetadata()"
              >
                <mat-icon>content_copy</mat-icon>
              </button>
            </div>

            <div class="findings-summary">
              <span class="summary-stat">
                <mat-icon [style.color]="'#c62828'">error</mat-icon>
                {{ examContradictions }} contradictions
              </span>
              <span class="summary-stat">
                <mat-icon [style.color]="'#f57c00'">warning</mat-icon>
                {{ examDiscrepancies }} discrepancies
              </span>
              <span class="summary-stat">
                <mat-icon [style.color]="'#388e3c'">check_circle</mat-icon>
                {{ examMatches }} matches
              </span>
              @if (examMissingTargets > 0) {
                <span class="summary-stat">
                  <mat-icon [style.color]="'#757575'">help</mat-icon>
                  {{ examMissingTargets }} missing
                </span>
              }
            </div>

            <div class="findings-drawer-body">
              @if (findingsOnCurrentPage.length > 0) {
                <div class="current-page-findings">
                  <span class="section-label">On this page:</span>
                  @for (f of findingsOnCurrentPage; track $index) {
                    <div class="finding-chip" [ngClass]="'severity-' + f.severity">
                      <mat-icon [style.color]="getSeverityColor(f.severity)">{{
                        getSeverityIcon(f.severity)
                      }}</mat-icon>
                      <span class="finding-chip-text"
                        >{{ f.detail | slice: 0 : 80 }}{{ f.detail.length > 80 ? '…' : '' }}</span
                      >
                    </div>
                  }
                </div>
              }

              <mat-accordion>
                @for (f of examFindings; track $index) {
                  <mat-expansion-panel>
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <mat-icon class="finding-severity-icon" [style.color]="getSeverityColor(f.severity)">{{
                          getSeverityIcon(f.severity)
                        }}</mat-icon>
                        {{ f.findingType }}
                      </mat-panel-title>
                      <mat-panel-description>
                        {{ f.edgeReference || f.edgeType }} · p{{ f.sourcePage }} → p{{ f.targetPage }}
                      </mat-panel-description>
                    </mat-expansion-panel-header>
                    <div class="finding-detail">
                      <p class="finding-detail-text">{{ f.detail }}</p>
                      @if (f.sourceExcerpt) {
                        <div class="finding-excerpt">
                          <strong>Source (p{{ f.sourcePage }}):</strong> {{ f.sourceExcerpt }}
                        </div>
                      }
                      @if (f.targetExcerpt) {
                        <div class="finding-excerpt">
                          <strong>Target (p{{ f.targetPage }}):</strong> {{ f.targetExcerpt }}
                        </div>
                      }
                      @if (f.recommendation) {
                        <div class="finding-recommendation">
                          <mat-icon>lightbulb</mat-icon>
                          {{ f.recommendation }}
                        </div>
                      }
                      <div class="finding-meta">
                        Confidence: {{ (f.confidence * 100).toFixed(0) }}% · {{ f.edgeType }}
                      </div>
                      <div class="finding-nav">
                        <button mat-stroked-button (click)="navigateToPage(f.sourceFileId, f.sourcePage)">
                          <mat-icon>open_in_new</mat-icon> Source (p{{ f.sourcePage }})
                        </button>
                        <button mat-stroked-button (click)="navigateToPage(f.targetFileId, f.targetPage)">
                          <mat-icon>open_in_new</mat-icon> Target (p{{ f.targetPage }})
                        </button>
                        <button
                          mat-stroked-button
                          class="copy-finding-btn"
                          (click)="copyFindingJson(f)"
                          matTooltip="Copy finding as JSON for bug reports"
                        >
                          <mat-icon>content_copy</mat-icon> Copy JSON
                        </button>
                        <button
                          mat-stroked-button
                          class="report-finding-btn"
                          (click)="reportFinding(f)"
                          [disabled]="!!reportingFinding"
                          matTooltip="File a GitHub issue for this finding"
                        >
                          <mat-icon>{{ reportingFinding === f ? 'hourglass_empty' : 'bug_report' }}</mat-icon>
                          {{ reportingFinding === f ? 'Filing...' : 'Report Issue' }}
                        </button>
                      </div>
                    </div>
                  </mat-expansion-panel>
                }
              </mat-accordion>
            </div>
          </div>
        }
      </div>

      <div class="graph-footer">
        <div class="legend">
          @for (item of legendItems; track item.type) {
            <span class="legend-item">
              <span class="legend-color" [style.background-color]="item.color"></span>
              {{ item.type }}
            </span>
          }
        </div>

        @if (selectedEdgeInfo) {
          <mat-card class="edge-detail">
            <mat-card-content>
              <div class="edge-detail-row">
                <strong>Type:</strong>
                <span class="edge-type-badge" [style.background-color]="selectedEdgeInfo.color">
                  {{ selectedEdgeInfo.type }}
                </span>
              </div>
              @if (selectedEdgeInfo.reference) {
                <div class="edge-detail-row"><strong>Reference:</strong> {{ selectedEdgeInfo.reference }}</div>
              }
              @if (selectedEdgeInfo.sourceLabel) {
                <div class="edge-detail-row"><strong>Source:</strong> {{ selectedEdgeInfo.sourceLabel }}</div>
              }
              @if (selectedEdgeInfo.targetLabel) {
                <div class="edge-detail-row"><strong>Target:</strong> {{ selectedEdgeInfo.targetLabel }}</div>
              }
            </mat-card-content>
          </mat-card>
        }
      </div>
    }

    @if (viewMode === 'data') {
      <div class="raw-data-wrapper">
        <pre class="raw-data-content code-block" [innerHTML]="rawDataHtml"></pre>
      </div>
    }
  </div>
}
